import React from 'react';
import { Loader2, Play } from 'lucide-react';
import { useMediaItemUrl } from './mediaUrl';
import { PostMediaItem, extractYouTubeId, hasDuration } from './mediaUtils';

export function DurationChip({ duration, className = '' }: { duration: string; className?: string }) {
  if (!hasDuration(duration)) return null;
  return (
    <span className={`absolute rounded-md bg-black/60 px-1.5 text-[11px] font-medium text-white ${className}`} data-testid="duration-chip">
      {duration}
    </span>
  );
}

export function ItemImage({ postId, item, alt, enabled, className }: {
  postId: string
  item: PostMediaItem
  alt: string
  enabled: boolean
  className: string
}) {
  const { url, error } = useMediaItemUrl(postId, item.id, enabled && item.kind === 'image');
  if (url) return <img src={url} alt={alt} draggable={false} className={className} />;
  return (
    <div className="flex h-full w-full items-center justify-center bg-[#050b18] text-xs text-white/50">
      {error ? 'Media unavailable' : enabled ? <Loader2 className="h-5 w-5 animate-spin text-white/40" /> : null}
    </div>
  );
}

export function ItemBackdrop({ item }: { item: PostMediaItem }) {
  const ytId = item.source === 'youtube' ? extractYouTubeId(item.videoUrl) : null;
  if (ytId) {
    return <img src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`} alt="" draggable={false} className="absolute inset-0 h-full w-full object-cover" />;
  }
  const tone = item.source === 'vidcast' ? 'from-[#5a3bb8] to-[#251650]' : 'from-[#2b3a4f] to-[#0c121b]';
  return <span className={`absolute inset-0 bg-gradient-to-br ${tone}`} />;
}

export default function MediaTile({ postId, item, index, more, title, style, onOpen }: {
  postId: string
  item: PostMediaItem
  index: number
  more: number
  title: string
  style?: React.CSSProperties
  onOpen: (index: number) => void
}) {
  return (
    <button
      style={style}
      type="button"
      aria-label={`Open item ${index + 1}`}
      onClick={() => onOpen(index)}
      className="group relative min-h-0 min-w-0 overflow-hidden bg-[#050b18] p-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#60a5fa]"
    >
      {item.kind === 'image' ? (
        <ItemImage postId={postId} item={item} alt={title} enabled className="h-full w-full object-cover transition group-hover:brightness-110" />
      ) : (
        <>
          <ItemBackdrop item={item} />
          <span className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-white/90 bg-[#080c18]/60">
            <Play className="ml-0.5 h-[18px] w-[18px] text-white" fill="white" />
          </span>
          <DurationChip duration={item.duration} className="bottom-1.5 right-1.5" />
        </>
      )}
      {more > 0 && (
        <span className="absolute inset-0 flex items-center justify-center bg-[#080c18]/60 text-3xl font-semibold tabular-nums text-white" data-testid="more-overlay">
          +{more}
        </span>
      )}
    </button>
  );
}
